import React, { useEffect, useState } from 'react';
import AudioPlayer from './AudioPlayer';
import AudioPlayerPlaylistPanel from './AudioPlayerPlaylistPanel';
import AudioPlayerSessionLayout from './AudioPlayerSessionLayout';
import { getOfflineTrack } from './audioOfflineStorage';
import { useLocalDevicePlaylist } from './useLocalDevicePlaylist';

function DeviceTrackPlayer({ track, theme, onQueueAdvance, queueHasNextTrack, equalizerGains, onEqualizerGainsChange }) {
  const [src, setSrc] = useState('');
  const [offlineKey, setOfflineKey] = useState('');
  const [title, setTitle] = useState('Audio');
  const [error, setError] = useState('');

  useEffect(() => {
    let revoke = null;
    let cancelled = false;

    async function resolve() {
      setSrc('');
      setOfflineKey('');
      setError('');
      if (!track) return;

      if (track.source === 'device' && track.objectUrl) {
        setSrc(track.objectUrl);
        setTitle(track.title);
        return;
      }

      if (track.source === 'offline' && track.offlineKey) {
        try {
          const row = await getOfflineTrack(track.offlineKey);
          if (cancelled || !row?.blob) {
            setError('Offline copy missing.');
            return;
          }
          revoke = URL.createObjectURL(row.blob);
          setSrc(revoke);
          setOfflineKey(track.offlineKey);
          setTitle(track.title || row.title || 'Offline track');
        } catch (e) {
          if (!cancelled) setError(e.message || 'Could not load offline track.');
        }
      }
    }

    resolve();
    return () => {
      cancelled = true;
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [track]);

  if (!track) return null;
  if (error) {
    return (
      <p className="audio-player__message audio-player__message--error" role="alert">
        {error}
      </p>
    );
  }
  if (!src) {
    return (
      <p className="audio-player__message" role="status">
        Loading…
      </p>
    );
  }

  return (
    <AudioPlayer
      key={track.trackId}
      theme={theme}
      src={src}
      title={title}
      subtitle={track.source === 'offline' ? 'Saved on this device' : track.fileName || 'Local file'}
      offlineKey={offlineKey}
      allowOffline={Boolean(offlineKey)}
      allowStreamToggle={Boolean(offlineKey)}
      onQueueAdvance={onQueueAdvance}
      queueHasNextTrack={queueHasNextTrack}
      equalizerGains={equalizerGains}
      onEqualizerGainsChange={onEqualizerGainsChange}
    />
  );
}

export default function LocalDeviceAudioSession({ theme = 'auto', playlistApi }) {
  const playlist = playlistApi;

  if (!playlist.currentTrack) {
    return (
      <p className="audio-player__message">
        Import MP3 files from your device or add tracks you saved for offline listening.
      </p>
    );
  }

  return (
    <AudioPlayerSessionLayout
      layoutContext="page"
      playlistResetKey={playlist.currentTrackId}
      player={
        <DeviceTrackPlayer
          track={playlist.currentTrack}
          theme={theme}
          onQueueAdvance={playlist.advanceTrack}
          queueHasNextTrack={playlist.hasNextTrackInQueue}
          equalizerGains={playlist.eqGains}
          onEqualizerGainsChange={playlist.setEqGains}
        />
      }
      playlist={
        <AudioPlayerPlaylistPanel
          playlistName={playlist.playlistName}
          onPlaylistNameChange={playlist.setPlaylistName}
          tracks={playlist.tracks}
          currentTrackId={playlist.currentTrackId}
          shuffle={playlist.shuffle}
          onShuffleChange={playlist.setShuffle}
          repeat={playlist.repeat}
          onRepeatChange={playlist.setRepeat}
          onAddCurrentTrack={() => playlist.setStatus('Use “Add files” above to grow this list.')}
          addCurrentLabel="Add current"
          onSelectTrack={playlist.setCurrentTrackId}
          onRemoveTrack={(trackId) => {
            playlist.setTracks((prev) => {
              const removed = prev.find((t) => t.trackId === trackId);
              if (removed?.source === 'device' && removed.objectUrl) {
                URL.revokeObjectURL(removed.objectUrl);
              }
              const next = prev.filter((t) => t.trackId !== trackId);
              if (playlist.currentTrackId === trackId) {
                playlist.setCurrentTrackId(next[0]?.trackId || '');
              }
              return next;
            });
          }}
          onLoadTracks={(loaded, meta) => {
            const deviceOnly = (loaded || []).filter((t) => t.source === 'device' || t.source === 'offline');
            if (deviceOnly.length !== (loaded || []).length) {
              playlist.setStatus('Only on-device tracks were loaded from this playlist.');
            }
            playlist.loadTracks(deviceOnly, meta);
          }}
          statusMessage={playlist.status}
          onStatusMessage={playlist.setStatus}
          eqGains={playlist.eqGains}
        />
      }
    />
  );
}
